import React, { ReactNode, useContext, useState } from 'react';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';
import { MarginProps, space } from 'styled-system';

import Box from 'components/Box';
import PageToolbar from 'components/PageToolbar';
import PrintHidden from 'components/PrintHidden';
import { BodySmall } from 'components/Text';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import { breakpoints, colors, gutterWidth, maxWidth, transition } from 'theme';
import AmpContext from 'utils/AmpContext';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants';

import Layout from './Layout';

export const NAME_MAP = {
  ...LISTING_TYPE_HUMAN_NAME_MAP,
  IDEALIST_DAY_EVENT: getText('event'),
  IMPORTED: getText('opportunity'),
};

const getMessage = ({ listingType, isOrg, isAdmin, isGroupAdmin }) => {
  const listingTypeName = NAME_MAP[listingType];
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  return isOrg
    ? sprintf(getText('Your organization posted this %(listingTypeName)s'), {
        listingTypeName,
      })
    : // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    isAdmin
    ? sprintf(getText('You are an administrator of this %(listingTypeName)s'), {
        listingTypeName,
      })
    : isGroupAdmin
    ? sprintf(getText('Your group posted this %(listingTypeName)s'), {
        listingTypeName,
      })
    : sprintf(getText('You posted this %(listingTypeName)s'), {
        listingTypeName,
      });
};

const Body = styled.div`
  display: -ms-grid;
  display: grid;
  grid-template-columns: 100%;
  -ms-grid-columns: 100%;
  grid-template-rows: max-content;
  -ms-grid-rows: max-content;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${gutterWidth / 2}px;
  position: relative;
  max-width: ${maxWidth[3]}px;
  @media (min-width: ${breakpoints[2]}) {
    padding: 0 ${gutterWidth}px;
    grid-template-columns: 288px calc(100% - 384px);
    -ms-grid-columns: 288px 96px calc(100% - 384px);
    grid-column-gap: 96px;
  }
  @media (min-width: ${breakpoints[3]}) {
    grid-template-columns: 288px 768px;
    -ms-grid-columns: 288px 96px 768px;
    grid-column-gap: 96px;
  }
`;

const Header = styled.div<MarginProps>`
  grid-row: 1;
  -ms-grid-row: 1;
  grid-column: 1;
  -ms-grid-column: 1;
  @media (min-width: ${breakpoints[2]}) {
    grid-column: 2;
    -ms-grid-column: 3;
  }
  ${space};
`;

const Main = styled.div`
  grid-column: 1;
  -ms-grid-column: 1;
  grid-row: 3;
  -ms-grid-row: 3;
  @media (min-width: ${breakpoints[2]}) {
    grid-column: 2;
    -ms-grid-column: 3;
    grid-row: 2;
    -ms-grid-row: 2;
  }
  ${space};
`;
const Sidebar = styled.div`
  background: white;
  border-radius: 4px;
  grid-row: 1 / 3;
  -ms-grid-row: 1;
  -ms-grid-row-span: 2;
  grid-column: 1;
  -ms-grid-column: 1;
  @media (max-width: ${breakpoints[2]}) {
    grid-row: 2;
    -ms-grid-row: 2;
    -ms-grid-row-span: 1;
  }
  ${space};
`;
const SidebarContent = styled.div`
  @media (min-width: ${breakpoints[2]}) {
    bottom: 40px;
    position: sticky;
    top: 150px;
  }
  ${space};
`;
const SidebarDetails = styled.div<{
  isAmp?: boolean;
  showSidebarDetails?: boolean;
}>`
  opacity: 1;
  transition: opacity ${transition}, height 0.25s ease;

  @media (max-width: ${breakpoints[2]}) {
    grid-row: 2;
    -ms-grid-row: 2;
    -ms-grid-row-span: 1;
  }
  ${({ isAmp }) =>
    isAmp
      ? ''
      : css`
          @media (min-width: ${breakpoints[2]}) {
            ${(props) =>
              // @ts-expect-error FIXME
              props.showSidebarDetails
                ? `
        opacity: 1;
      `
                : `
                opacity: 0; pointer-events: none;

                > div {
                  visibility: hidden;
                }
          `}
          }
        `};
  ${space};
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ListingLayout = ({
  adminActions,
  toolbar,
  isOrg,
  isAdmin,
  isGroupAdmin,
  listingType,
  sidebar,
  sidebarButtons,
  sidebarLinks,
  sidebarTitle,
  sidebarImage,
  header,
  buttonBar,
  main,
  banner,
  footer,
  navSection,
  localeUrl,
}: {
  adminActions?: ReactNode;
  toolbar?: ReactNode;
  isOrg?: boolean;
  isAdmin?: boolean;
  isGroupAdmin?: boolean;
  listingType?:
    | 'JOB'
    | 'INTERNSHIP'
    | 'VOLOP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT'
    | 'IMPORTED';
  sidebarTitle?: ReactNode | null | undefined;
  sidebarImage: ReactNode | null | undefined;
  sidebar: ReactNode;
  sidebarButtons?: ReactNode;
  sidebarLinks?: ReactNode;
  header: ReactNode;
  buttonBar?: ReactNode;
  main: ReactNode;
  banner?: ReactNode;
  footer?: ReactNode;
  navSection?: 'MAIN' | 'STAFF';
  localeUrl: Record<string, string>;
}) => {
  const isAmp = useContext(AmpContext);
  const [showSidebarDetails, setShowSidebarDetails] = useState();
  return (
    <Layout
      buttonBar={buttonBar}
      defaultSearchType={listingType === 'IMPORTED' ? 'VOLOP' : listingType}
      navSection={navSection}
      localeUrl={localeUrl}
      defaultWithSearchHero={false}
    >
      {toolbar ||
        (adminActions && (
          <PageToolbar
            centered
            message={
              listingType && listingType !== 'IMPORTED'
                ? getMessage({
                    listingType,
                    isOrg,
                    isAdmin,
                    isGroupAdmin,
                  })
                : undefined
            }
            tools={adminActions}
          />
        ))}
      {banner}
      <Body>
        <ScrollWatcherContainer offset={[150, 150, 150, 150]}>
          {({ isFullyAboveViewport, isFullyInViewport }) => {
            if (isFullyAboveViewport) {
              // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
              setShowSidebarDetails(true);
            } else if (isFullyInViewport) {
              // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
              setShowSidebarDetails(false);
            }

            return (
              <Header
                mr={[0, '20px', '40px', '100px']}
                mt={[3, 3, 3, 4]}
                mb={[0, 0, 0, 3]}
              >
                {header}
              </Header>
            );
          }}
        </ScrollWatcherContainer>

        <Sidebar>
          {/* @ts-expect-error Fix types */}
          <SidebarContent my={[0, 0, 0, 4]}>
            <BodySmall lineHeight="normal">
              {sidebarImage}
              <Box aria-hidden="true">{sidebarTitle}</Box>
              {sidebar}
              <SidebarDetails
                showSidebarDetails={showSidebarDetails}
                isAmp={isAmp}
              >
                <Box aria-hidden="true">{sidebarButtons}</Box>
                {sidebarLinks}
              </SidebarDetails>
            </BodySmall>
          </SidebarContent>
        </Sidebar>

        {/* @ts-expect-error Fix types */}
        <Main mr={[0, '20px', '40px', '100px']}>{main}</Main>
      </Body>
      {footer && (
        <PrintHidden>
          <Box
            py={[gutterWidth, null, null, gutterWidth * 2]}
            bg={colors.fog}
            mb={['-1px', null, null, -55]}
            textAlign="center"
          >
            <Box
              px={[gutterWidth / 2, null, null, gutterWidth]}
              maxWidth={maxWidth[3]}
              m="0 auto"
            >
              {footer}
            </Box>
          </Box>
        </PrintHidden>
      )}
    </Layout>
  );
};

export default ListingLayout;
